import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AdminAuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if token exists on mount
    const storedToken = localStorage.getItem('admin_token');
    const storedAdmin = localStorage.getItem('admin_username');
    
    if (storedToken && storedAdmin) {
      setToken(storedToken);
      setAdmin({ username: storedAdmin });
    }
    setLoading(false);
  }, []);

  // Setup axios interceptor to handle 401 errors globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - logout and redirect
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_username');
          setToken(null);
          setAdmin(null);
          if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
            window.location.href = '/admin/login?session=expired';
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, {
        username,
        password
      });
      
      const { access_token, username: adminUsername } = response.data;
      
      localStorage.setItem('admin_token', access_token);
      localStorage.setItem('admin_username', adminUsername);
      
      setToken(access_token);
      setAdmin({ username: adminUsername });
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (username, password) => {
    try {
      await axios.post(`${API_URL}/api/admin/register`, {
        username,
        password
      });
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    setToken(null);
    setAdmin(null);
  };

  const getAuthHeaders = () => {
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  // Auto-logout on 401 errors
  const handleAuthError = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    setToken(null);
    setAdmin(null);
    window.location.href = '/admin/login?expired=true';
  };

  const value = {
    admin,
    token,
    loading,
    login,
    register,
    logout,
    getAuthHeaders,
    handleAuthError,
    isAuthenticated: !!token
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};